import { useState, type ReactNode } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { useQuery, type QueryKey } from '@tanstack/react-query';

import { cn, POPOVER_MOTION } from '../../shared/lib/cn';
import { Button } from '../../shared/ui/button';
import { PlusIcon, SearchIcon } from '../../shared/ui/icons';
import { Input } from '../../shared/ui/input';

const PANEL = 'rounded-card border border-border-raised bg-surface-raised';

export interface IFacetBucket {
  value: string | null;
  count: number;
  label?: string | null;
  deleted?: boolean;
}

export interface IFacetGroup<F extends string> {
  title: string;
  facets: readonly { field: F; label: string }[];
}

/** How one list's facets are counted and worded; the panel itself knows neither. */
export interface IFacetSource<F extends string> {
  groups: readonly IFacetGroup<F>[];
  label: (field: F) => string;
  selected: (field: F) => string[];
  /** Null for a field with no index behind it: its values cannot be listed. */
  count: (field: F) => { queryKey: QueryKey; queryFn: () => Promise<IFacetBucket[]> } | null;
  text: (field: F, bucket: IFacetBucket) => string;
  render: (field: F, bucket: IFacetBucket) => ReactNode;
  /** Why a field has no values and where that is changed; the plain line when absent. */
  emptyNote?: (field: F) => ReactNode;
}

const Row = ({
  on,
  onClick,
  children,
}: {
  on?: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex h-control-sm w-full items-center gap-inset border-l-2 px-3 text-left text-data',
      on
        ? 'border-l-accent bg-highlight text-primary'
        : 'border-l-transparent text-muted hover:bg-hover-raised',
    )}
  >
    {children}
  </button>
);

const ValueList = <F extends string>({
  field,
  source,
  onApply,
}: {
  field: F;
  source: IFacetSource<F>;
  onApply: (values: string[]) => void;
}) => {
  const [draft, setDraft] = useState<string[]>(source.selected(field));
  const [needle, setNeedle] = useState('');
  const counting = source.count(field);

  const facets = useQuery({
    queryKey: counting?.queryKey ?? ['facet-uncounted', field],
    queryFn: counting?.queryFn ?? (() => Promise.resolve([])),
    enabled: counting !== null,
  });

  const buckets = (facets.data ?? []).filter((bucket) => {
    const hay = `${bucket.value ?? ''} ${source.text(field, bucket)}`.toLowerCase();
    return hay.includes(needle.toLowerCase());
  });

  const toggle = (value: string) =>
    setDraft((current) =>
      current.includes(value) ? current.filter((one) => one !== value) : [...current, value],
    );

  return (
    <div className="flex flex-col">
      <Input
        bare
        type="search"
        leading={<SearchIcon size={14} />}
        value={needle}
        onChange={(event) => setNeedle(event.target.value)}
        placeholder="Filter values"
        aria-label="Filter values"
        className="border-b border-border-raised"
      />

      <div className="max-h-facets overflow-y-auto py-1">
        {/* A disabled query stays `isPending` forever, so uncountable fields must be branched out first. */}
        {counting === null ? (
          <p className="px-3 py-2 text-meta text-muted">
            This one has no index, so its values cannot be listed. Click the value in a row instead.
          </p>
        ) : (
          facets.isPending && <p className="px-3 py-2 text-meta text-muted">Counting…</p>
        )}
        {facets.isError && (
          <p className="px-3 py-2 text-meta text-danger">Could not count these values.</p>
        )}
        {buckets.map((bucket) => {
          const value = bucket.value ?? '';
          const on = draft.includes(value);
          return (
            <Row key={value} on={on} onClick={() => toggle(value)}>
              <span
                aria-hidden
                className={cn(
                  'size-4 shrink-0 rounded-tag border',
                  on ? 'border-accent bg-accent' : 'border-suppressed',
                )}
              />
              {source.render(field, bucket)}
              <span className="ml-auto pl-2 font-mono text-micro text-faint">
                {bucket.count.toLocaleString('en-US')}
              </span>
            </Row>
          );
        })}
        {counting !== null && !facets.isPending && buckets.length === 0 && (
          <p className="px-3 py-2 text-meta text-muted">
            {source.emptyNote?.(field) ?? 'No values in this window.'}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 border-t border-border-raised px-3 py-3">
        <span className="font-mono text-micro text-faint">{draft.length} selected</span>
        <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setDraft([])}>
          Clear
        </Button>
        <Popover.Close asChild>
          <Button size="sm" onClick={() => onApply(draft)}>
            Apply
          </Button>
        </Popover.Close>
      </div>
    </div>
  );
};

interface IFacetPanelProps<F extends string> {
  source: IFacetSource<F>;
  onChange: (field: F, values: string[]) => void;
  field?: F;
  trigger?: ReactNode;
}

export const FacetPanel = <F extends string>({
  source,
  onChange,
  field,
  trigger,
}: IFacetPanelProps<F>) => {
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<F | null>(field ?? null);

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setChosen(field ?? null);
      }}
    >
      <Popover.Trigger asChild>
        {trigger ?? (
          <Button variant="ghost" size="sm">
            <PlusIcon size={13} />
            Filter
          </Button>
        )}
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className={cn(
            PANEL,
            'w-facets',
            'origin-(--radix-popover-content-transform-origin)',
            POPOVER_MOTION,
          )}
        >
          {chosen ? (
            <ValueList
              field={chosen}
              source={source}
              onApply={(values) => onChange(chosen, values)}
            />
          ) : (
            <div className="max-h-facets overflow-y-auto py-1">
              {source.groups.map((group) => (
                <div key={group.title}>
                  <p className="px-3 pt-2 pb-1 text-micro font-medium tracking-group text-faint">
                    {group.title}
                  </p>
                  {group.facets.map((facet) => (
                    <Row key={facet.field} onClick={() => setChosen(facet.field)}>
                      <span>{source.label(facet.field)}</span>
                    </Row>
                  ))}
                </div>
              ))}
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
